import React, { useState, useEffect } from "react";
import { Typography, Box, Snackbar, Alert } from "@mui/material";
import { Button, TextField, Container } from "@/components/atoms";
import { useUserContext } from "@/providers/contexts";
import { isValidEmail, isValidPassword } from "@/utils/validators";
import { useLoginUser, useForgotPassword } from "@/hooks";
import GoogleIcon from "@mui/icons-material/Google";

export const Login = () => {
  /* --------------------- Hooks --------------------- */
  const {
    data,
    loading: loginLoading,
    error: loginError,
    loginUser,
  } = useLoginUser();

  const {
    data: forgotPasswordData,
    loading: forgotPasswordLoading,
    error: forgotPasswordError,
    forgotPassword,
  } = useForgotPassword();

  const { dispatch, loading } = useUserContext();

  /* --------------------- State --------------------- */

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);

  const [snackBar, setSnackBar] = useState<{
    state: boolean;
    msg: string;
    severity: "error" | "success";
  }>({ state: false, msg: "", severity: "success" });

  /* --------------------- Lifecycle Methods --------------------- */

  useEffect(() => {
    setEmailError(
      email && !isValidEmail(email) ? "Invalid email address" : null,
    );
    setPasswordError(
      password && !isValidPassword(password)
        ? "Password must be at least 8 characters."
        : null,
    );
  }, [email, password]);

  useEffect(() => {
    setIsFormValid(
      !emailError && !passwordError && email !== "" && password !== "",
    );
  }, [emailError, passwordError, email, password]);

  useEffect(() => {
    if (data?.success) {
      setSnackBar({
        state: true,
        msg: "Login successful. Redirecting...",
        severity: "success",
      });

      localStorage.setItem("authToken", data.access_token);
      localStorage.setItem("refreshToken", data.refresh_token);
      localStorage.setItem("userId", data.user.id.toString());
      dispatch({ type: "SET_USER_ID", payload: data.user.id });
      dispatch({ type: "SET_AUTHTOKEN", payload: data.access_token });

      setTimeout(() => {
        window.location.href = "/";
      }, 1000);
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (loginError) {
      setSnackBar({
        state: true,
        msg: loginError,
        severity: "error",
      });
    }
  }, [loginError]);

  useEffect(() => {
    if (forgotPasswordData?.success) {
      setSnackBar({
        state: true,
        msg: forgotPasswordData.message,
        severity: "success",
      });
    }
  }, [forgotPasswordData]);

  useEffect(() => {
    if (forgotPasswordError) {
      setSnackBar({
        state: true,
        msg: forgotPasswordError,
        severity: "error",
      });
    }
  }, [forgotPasswordError]);

  /* --------------------- Handles --------------------- */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) return;

    await loginUser({ email: email.trim(), password });
  };

  return (
    <Container
      componentName="login"
      sx={{
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Snackbar
        open={snackBar.state}
        autoHideDuration={6000}
        onClose={() => setSnackBar(prev => ({ ...prev, state: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackBar.severity}
          variant="filled"
          onClose={() => setSnackBar(prev => ({ ...prev, state: false }))}
        >
          {snackBar.msg}
        </Alert>
      </Snackbar>

      <Box
        justifyContent="center"
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{ margin: 2, width: "100%" }}
        data-testid="login-title"
      >
        <Typography variant="h4" gutterBottom>
          Login
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            width: "100%",
            maxWidth: "400px",
            border: "none",
          }}
          data-testid="login-form"
        >
          <TextField
            componentName="login-email"
            placeholder="Email"
            variant="outlined"
            value={email}
            label="Email"
            onChange={e => setEmail(e.target.value)}
            required
            error={!!emailError}
            helperText={emailError}
            fullWidth
          />
          <TextField
            componentName="login-password"
            type="password"
            variant="outlined"
            placeholder="Password"
            value={password}
            label="Password"
            onChange={e => setPassword(e.target.value)}
            required
            error={!!passwordError}
            helperText={passwordError}
            fullWidth
          />
          <Button
            componentName="login-button"
            color="primary"
            type="submit"
            disabled={!isFormValid || loading || loginLoading}
            loading={loginLoading}
          >
            Login
          </Button>
        </Box>

        <Box
          flexDirection="row"
          display="flex"
          columnGap={2}
          justifyContent="flex-start"
          sx={{ mt: 1, mb: 4 }}
          data-testid="login-links"
        >
          <Button
            componentName="create-account-button"
            variant="text"
            color="primary"
            href="/register"
          >
            Create Account
          </Button>

          <Button
            componentName="forgot-password-button"
            variant="text"
            color="primary"
            onClick={() => {
              if (!email || !isValidEmail(email)) {
                setEmailError("Enter a valid email to reset your password.");
                return;
              }
              forgotPassword({ email: email.trim() });
            }}
            disabled={forgotPasswordLoading}
          >
            Forgot Password
          </Button>
        </Box>

        <Typography>OR</Typography>
        <Button
          componentName="google-login-button"
          variant="outlined"
          startIcon={<GoogleIcon />}
          color="primary"
          sx={{ my: 2 }}
          onClick={() => {
            const backendBase = import.meta.env.VITE_API_BASE_URL;
            if (!backendBase) {
              console.error(
                "Missing VITE_API_BASE_URL in environment variables",
              );
              return;
            }
            window.location.assign(`${backendBase}/login/google-oauth2/`);
          }}
        >
          Continue with Google
        </Button>
      </Box>
    </Container>
  );
};
